import { ReactNode } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from "@carbon/react";
import {
  DataTableColumn,
  DataTableRow,
} from "../../components/carbon/DataTable/DataTable";
import { Pill } from "../../components/UI/Pill/Pill";
import { AllOrdersResponse, MaterialType, OrderResponse } from "../../types";
import {
  cutIdOnFirstHyphen,
  formatBiddingDateTime,
  formatDate,
  labelType,
} from "../../utils";

/** The `t` returned by `useTranslation()` — typed loosely (same convention `materialRows.tsx`
 * uses) since every call site here only ever calls it with a plain key. */
type TFunc = (key: string) => string;

type NavType = "SIMPLE" | "JOINT";

/** One material line of an order, for the expandable detail row. */
export interface OrderMaterialLine {
  key: string;
  ean: string;
  material: string;
  quantity: number;
  measurementUnit: string;
  startSupplyDate?: string;
  endSupplyDate?: string;
  /** A service has no volume to show (SCRUM-55). */
  isService: boolean;
}

/**
 * One display item per ORDER (Task 3 flattened `JointOrder`s into a summary + one always-visible
 * sub-row per material because the shared `DataTable` had no row-expansion; it does now, so each
 * order is a single row and its material breakdown lives in an expandable detail row instead).
 *
 * `rowId`/`navId` stay separate: a `JOINT` summary's `navId` is `jointOrders[0].id`, the id the
 * joint bid view navigates on. `rowId` gets a `joint-` prefix so keys stay unique.
 */
interface OrderListItem {
  rowId: string;
  navType: NavType;
  navId: string;
  multipleOrderId: string | null;
  name: string;
  internalCode: string;
  startDateISO: string;
  finishDateISO: string;
  opensAtISO?: string | null;
  closesAtISO?: string | null;
  suppliersCount: number;
  responsible: string;
  /** Buyer company name — shown on the seller RFQ table (`supplierRfqHeaders`). */
  company: string;
  /** Localized bidding-type label (Normal/Inversa/Conjunta…) — shown on the seller RFQ table. */
  typeLabel: string;
  /** Seller biddings only: whether this seller already quoted, (simple) that offer's id — for routing
   * a quoted row to the seller bid view — and how many of the RFQ's line items are offered of the
   * total. Default to not-quoted on the other lists. */
  hasOffered: boolean;
  offerId: string | null;
  offeredItemCount: number;
  totalItemCount: number;
  /** Seller biddings only: whether this seller won any line (SUCCEED + a selected offer). */
  awardedToMe?: boolean;
  orderObject: OrderResponse;
  materials: OrderMaterialLine[];
}

const responsibleName = (order: OrderResponse) =>
  `${order.user.firstName} ${order.user.lastName}`;

const materialLine = (order: OrderResponse): OrderMaterialLine => ({
  key: order.id,
  ean: order.material?.ean || "-",
  material: order.material?.globalMaterialName || "-",
  quantity: order.quantity,
  measurementUnit: order.measurementUnit,
  startSupplyDate: order.startSupplyDate,
  endSupplyDate: order.endSupplyDate,
  isService: order.material?.category?.type === MaterialType.SERVICES,
});

/**
 * Flattens `AllOrdersResponse['orders']` into a display-ready list, one item per order. Shared by
 * `Orders.tsx` (`useOrders`) and `CompanyOrders.tsx` (`useAllOrders`) — both return the identical
 * `AllOrdersResponse` shape — so one flattener covers both.
 */
export const flattenOrdersForList = (
  ordersData: AllOrdersResponse | undefined
): OrderListItem[] => {
  if (!ordersData?.orders) return [];

  const items: OrderListItem[] = [];

  ordersData.orders.forEach((orderItem) => {
    if (orderItem.type === "SIMPLE" && orderItem.simpleOrder) {
      const order = orderItem.simpleOrder;
      items.push({
        rowId: order.id,
        navType: "SIMPLE",
        navId: order.id,
        multipleOrderId: order.multipleOrder?.id ?? null,
        name: order.name,
        internalCode: order.internalCode
          ? cutIdOnFirstHyphen(order.internalCode)
          : "-",
        startDateISO: order.startDate,
        finishDateISO: order.finishDate,
        opensAtISO: order.opensAt,
        closesAtISO: order.closesAt,
        suppliersCount: order.suppliers?.length || 0,
        responsible: responsibleName(order),
        company: order.company?.name || "-",
        typeLabel: labelType(order.type) ?? "-",
        hasOffered: orderItem.hasOffered ?? false,
        offerId: orderItem.offerId ?? null,
        offeredItemCount: orderItem.offeredItemCount ?? 0,
        totalItemCount: orderItem.totalItemCount ?? 1,
        awardedToMe: orderItem.awardedToMe,
        orderObject: order,
        materials: [materialLine(order)],
      });
      return;
    }

    if (orderItem.type === "JOINT" && orderItem.jointOrders) {
      const jointOrders = orderItem.jointOrders;
      const firstOrder = jointOrders[0];
      if (!firstOrder) return;

      items.push({
        rowId: `joint-${firstOrder.id}`,
        navType: "JOINT",
        navId: firstOrder.id,
        multipleOrderId: firstOrder.multipleOrder?.id ?? null,
        name: orderItem.multipleOrderName,
        internalCode: firstOrder.internalCode
          ? cutIdOnFirstHyphen(firstOrder.internalCode)
          : "-",
        startDateISO: firstOrder.startDate,
        finishDateISO: firstOrder.finishDate,
        opensAtISO: firstOrder.opensAt,
        closesAtISO: firstOrder.closesAt,
        suppliersCount: firstOrder.suppliers?.length || 0,
        responsible: responsibleName(firstOrder),
        company: firstOrder.company?.name || "-",
        typeLabel: labelType(firstOrder.type) ?? "-",
        hasOffered: orderItem.hasOffered ?? false,
        offerId: orderItem.offerId ?? null,
        offeredItemCount: orderItem.offeredItemCount ?? 0,
        totalItemCount: orderItem.totalItemCount ?? jointOrders.length,
        awardedToMe: orderItem.awardedToMe,
        orderObject: firstOrder,
        materials: jointOrders.map(materialLine),
      });
    }
  });

  return items;
};

export interface OrderRow extends DataTableRow {
  id: string;
  navType: NavType;
  navId: string;
  multipleOrderId: string | null;
  internalCode: ReactNode;
  name: ReactNode;
  startDate: ReactNode;
  finishDate: ReactNode;
  suppliersCount: ReactNode;
  /** How many item lines the RFQ has: the seller tables show it instead of a summed volume (SCRUM-55). */
  itemsCount: ReactNode;
  responsible: ReactNode;
  company: ReactNode;
  type: ReactNode;
  state: ReactNode;
  /** Carried for seller-bidding row routing; not rendered columns. */
  hasOffered: boolean;
  offerId: string | null;
  /** Seller biddings "your offer" column: a tag showing offered n/m (or not-offered). */
  offerStatus: ReactNode;
  /** Carried for the expandable detail row; not a rendered column. */
  materials: OrderMaterialLine[];
}

/** The seller-biddings "your offer" tag: green "offered n/m" when quoted, a neutral tag otherwise. */
const offerStatusCell = (item: OrderListItem, t: TFunc): ReactNode =>
  item.hasOffered ? (
    <Tag type="green" size="sm">
      {`${t("offer.offeredTag")} ${item.offeredItemCount}/${item.totalItemCount}`}
    </Tag>
  ) : (
    <Tag type="gray" size="sm">
      {t("offer.notOfferedTag")}
    </Tag>
  );

const nameCell = (item: OrderListItem): ReactNode => (
  <span className="orders__name">
    {item.name}
    {item.materials.length > 1 ? (
      <span className="orders__joint-count">{item.materials.length}</span>
    ) : null}
  </span>
);

/** Maps flattened list items to `DataTable` rows — one row per order, each carrying its
 * `materials` for the expandable detail row (see `orderExpandedContent`). */
export const buildOrderRows = (
  ordersData: AllOrdersResponse | undefined,
  t?: TFunc,
  isSeller = false
): OrderRow[] =>
  flattenOrdersForList(ordersData).map((item) => ({
    id: item.rowId,
    navType: item.navType,
    navId: item.navId,
    multipleOrderId: item.multipleOrderId,
    internalCode: item.internalCode,
    name: nameCell(item),
    startDate: formatBiddingDateTime(item.opensAtISO, item.startDateISO),
    finishDate: formatBiddingDateTime(item.closesAtISO, item.finishDateISO),
    suppliersCount: item.suppliersCount,
    itemsCount: item.materials.length,
    responsible: item.responsible,
    company: item.company,
    type: item.typeLabel,
    state: (
      <Pill
        order={item.orderObject}
        isSeller={isSeller}
        selected={item.awardedToMe ?? true}
      />
    ),
    hasOffered: item.hasOffered,
    offerId: item.offerId,
    offerStatus: t ? offerStatusCell(item, t) : null,
    materials: item.materials,
  }));

/** The expandable detail row content for an order: a Carbon table of its material line(s). */
export const orderExpandedContent = (row: OrderRow, t: TFunc): ReactNode => {
  // A service has no volume: the column goes when every line is one, a mixed RFQ shows "-".
  const showVolume = row.materials.some((line) => !line.isService);
  return (
    <Table size="sm" className="orders__materials-table">
      <TableHead>
        <TableRow>
          <TableHeader>{t("tenders.tenderSummary.code")}</TableHeader>
          <TableHeader>{t("tenders.tenderSummary.material")}</TableHeader>
          {showVolume && (
            <TableHeader>{t("tenders.tenderSummary.volume")}</TableHeader>
          )}
          <TableHeader>{t("tenders.tenderSummary.tenderPeriod")}</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {row.materials.map((line) => (
          <TableRow key={line.key}>
            <TableCell>{line.ean}</TableCell>
            <TableCell>{line.material}</TableCell>
            {showVolume && (
              <TableCell>
                {line.isService
                  ? "-"
                  : `${line.quantity} ${line.measurementUnit ?? ""}`.trim()}
              </TableCell>
            )}
            <TableCell>
              {line.startSupplyDate && line.endSupplyDate
                ? line.startSupplyDate === line.endSupplyDate
                  ? formatDate(line.startSupplyDate)
                  : `${formatDate(line.startSupplyDate)} - ${formatDate(
                      line.endSupplyDate
                    )}`
                : "-"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export const orderListHeaders = (t: TFunc): DataTableColumn<OrderRow>[] => [
  { key: "internalCode", header: t("tenders.form.internalCode") },
  { key: "name", header: t("tenders.form.name") },
  { key: "startDate", header: t("tenders.tenderSummary.start") },
  { key: "finishDate", header: t("tenders.tenderSummary.end") },
  { key: "suppliersCount", header: t("tenders.form.suppliers") },
  { key: "responsible", header: t("tenders.form.responsible") },
  { key: "state", header: t("tenders.form.state") },
];

/**
 * Columns for the SELLER's unified RFQ table (the supplier home). Reuses the same `OrderRow`s
 * `buildOrderRows` produces, but shows the seller-relevant view — the buyer `company`, the bidding
 * `type`, and when the RFQ closes (`finishDate`) — instead of the buyer-internal `responsible`/
 * `suppliersCount`/`internalCode` columns. The expand chevron + material detail row and the
 * row-click navigation are identical to the buyer table.
 */
export const supplierRfqHeaders = (t: TFunc): DataTableColumn<OrderRow>[] => [
  { key: "name", header: t("tenders.form.name") },
  { key: "company", header: t("tenders.form.company") },
  { key: "itemsCount", header: t("tenders.tenderSummary.items") },
  { key: "finishDate", header: t("opportunities.table.closes") },
  { key: "state", header: t("tenders.form.state") },
];

/**
 * Columns for the seller's /biddings table — the RFQ columns plus a "your offer" tag showing whether
 * this seller has quoted and how many of the RFQ's line items (offered n/m). Requires `buildOrderRows`
 * to be called WITH `t` so the `offerStatus` cell is populated.
 */
export const supplierBiddingHeaders = (
  t: TFunc
): DataTableColumn<OrderRow>[] => [
  { key: "name", header: t("tenders.form.name") },
  { key: "company", header: t("tenders.form.company") },
  { key: "itemsCount", header: t("tenders.tenderSummary.items") },
  { key: "type", header: t("tenders.form.tenderType") },
  { key: "finishDate", header: t("opportunities.table.closes") },
  { key: "offerStatus", header: t("offer.yourOffer") },
  { key: "state", header: t("tenders.form.state") },
];

/**
 * Row-click navigation — a `SIMPLE` order goes to `BuyerBid` (`/orders/:id`), a `JOINT` row goes
 * to `JointBuyerBid` (`/orders/multiple/:id`) carrying the group's `multipleOrderId` query param.
 */
export const navigateToOrder = (
  navigate: (path: string) => void,
  row: OrderRow
): void => {
  if (row.navType === "SIMPLE") {
    navigate(`/orders/${row.navId}`);
  } else {
    navigate(
      `/orders/multiple/${row.navId}?multipleOrderId=${row.multipleOrderId}`
    );
  }
};

/**
 * Row-click navigation for the SELLER's all-states biddings list (`/biddings`). A row the seller has
 * already quoted goes to their bid view (`/offer/:offerId` simple, `/offer/multiple/:orderId` joint);
 * one they haven't goes to the submission page (`/orders/:id`), which itself gates on state (open →
 * form, closed → notice). This avoids the submission page's redirect-loop for already-quoted orders.
 */
export const navigateToSellerBidding = (
  navigate: (path: string) => void,
  row: OrderRow
): void => {
  if (row.navType === "JOINT") {
    const base = row.hasOffered ? "/offer/multiple" : "/orders/multiple";
    navigate(`${base}/${row.navId}?multipleOrderId=${row.multipleOrderId}`);
    return;
  }
  if (row.hasOffered && row.offerId) {
    navigate(`/offer/${row.offerId}`);
  } else {
    navigate(`/orders/${row.navId}`);
  }
};
